/**
 * ライトボックス（FR-LB）。埋め込みのビューワを画面全面へ持ち上げる。
 * Esc で閉じ、開いている間はフォーカスをビューワ内に閉じ込め、閉じたら元の要素へ戻す。
 *
 * 役割・モーダル・Tab の閉じ込め・復帰は `a11y/dialog.ts` の共通処理へ寄せてあり、
 * ここが自分で持つのは **Esc の順序**だけ。全面表示はサイドパネルより外側なので、
 * Esc は「パネル → ライトボックス」の順に閉じる（`ui/shell.ts` の規約）。
 */
import { escapeClaimed, openDialog } from '../a11y/dialog';
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';

export class Lightbox {
  private open = false;
  private release: (() => void) | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly registry: DisposeRegistry,
    private readonly onChange: () => void,
  ) {}

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  show(): void {
    if (this.open) return;
    const doc = this.root.ownerDocument;
    this.root.classList.add(cls('lightbox'));
    this.open = true;

    const releaseKey = this.registry.listen(
      doc,
      'keydown',
      (event) => this.onKeydown(event as KeyboardEvent),
      true,
    );
    // 初期フォーカスはルート自身。キー操作の受け口がルートにあるため、ここを
    // 最初の操作子（ツールバーの先頭ボタン）へ移すと矢印キーの宛先が変わってしまう。
    const releaseModal = openDialog(this.root, { initialFocus: this.root });
    this.release = () => {
      releaseKey();
      releaseModal();
    };

    this.onChange();
  }

  close(): void {
    if (!this.open) return;
    this.root.classList.remove(cls('lightbox'));
    this.open = false;
    this.release?.();
    this.release = null;
    this.onChange();
  }

  dispose(): void {
    if (this.open) this.close();
  }

  /** Esc で閉じる（Tab の閉じ込めは共通処理の担当）。 */
  private onKeydown(event: KeyboardEvent): void {
    // 内側（サイドパネル）が先に引き取った Esc は奪わない — 1 回で 2 段閉じない
    if (event.defaultPrevented) return;
    // 上に重なったモーダル（解錠フォーム・注釈ノート）が先に閉じる
    if (escapeClaimed()) return;
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }
}
